import { ranges } from '@/components/DatePicker';
import * as S from '@/components/HelpBanner/styled';
import { PATH } from '@/constants';
import { ShipmentStatus } from '@/constants/shipment';
import { camelToRegularText, capitalizeWords, handleOpenChat } from '@/helpers';
import { convertUtcHourToLocalTimezone, formatAnalyticsTooltipTitle } from '@/helpers/time';
import { EShipmentType } from '@/pages/Shipments/config';
import { useGetAnalyticsShipmentOverviewQuery } from '@/redux/api/api.caller';
import { tokenSelector } from '@/redux/features/auth.slice';
import { setPermanentDismissId } from '@/redux/features/common.slice';
import { datePickerSelector, titleDatePickerSelector } from '@/redux/features/dashboard.slice';
import shipmentSlice, { initialStateFilter, shipmentFilterSelector } from '@/redux/features/shipment.slice';
import { RootState } from '@/redux/store';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  InlineGrid,
  InlineStack,
  ProgressBar,
  SkeletonBodyText,
  Text,
  Tooltip,
} from '@shopify/polaris';
import { ArrowRightIcon, RefreshIcon, XIcon } from '@shopify/polaris-icons';
import { BarChart, DataSeries, DonutChart } from '@shopify/polaris-viz';
import { memo, useEffect, useRef, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useUpgradeTracking } from '@/hooks/useUpgradeTracking';
import { StyledShipmentsOverview } from './styled';

const CustomIcon = memo(function CustomIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={60} height={60} viewBox="0 0 60 60" fill="none">
      <g clipPath="url(#clip0_3367_11406)">
        <path
          d="M58.8637 35.7778L45.7387 30.1528C45.5055 30.0522 45.2541 30.0002 45 30.0002C44.7459 30.0002 44.4945 30.0522 44.2613 30.1528L31.1363 35.7778C30.7991 35.9222 30.5118 36.1623 30.3098 36.4685C30.1078 36.7746 30.0001 37.1332 30 37.5V52.5C29.9999 52.8669 30.1075 53.2258 30.3095 53.5321C30.5115 53.8384 30.799 54.0787 31.1363 54.2231L44.2613 59.8481C44.4946 59.9485 44.746 60.0003 45 60.0003C45.254 60.0003 45.5054 59.9485 45.7387 59.8481L58.8637 54.2231C59.201 54.0787 59.4885 53.8384 59.6905 53.5321C59.8925 53.2258 60.0001 52.8669 60 52.5V37.5C59.9999 37.1332 59.8922 36.7746 59.6902 36.4685C59.4882 36.1623 59.2009 35.9222 58.8637 35.7778ZM45 41.0859L43.0491 40.2497L51.105 36.5316L53.3653 37.5L45 41.0859ZM45 33.915L46.4644 34.5431L38.4084 38.2612L36.6338 37.5L45 33.915ZM33.75 40.3434L36.5625 41.5491V46.875C36.5625 47.3723 36.76 47.8492 37.1117 48.2008C37.4633 48.5525 37.9402 48.75 38.4375 48.75C38.9348 48.75 39.4117 48.5525 39.7633 48.2008C40.115 47.8492 40.3125 47.3723 40.3125 46.875V43.1559L43.125 44.3616V55.2816L33.75 51.2644V40.3434ZM46.875 55.2816V44.3625L56.25 40.3444V51.2653L46.875 55.2816ZM25.3125 49.6875H5.625C5.12787 49.687 4.65125 49.4893 4.29972 49.1378C3.9482 48.7863 3.7505 48.3096 3.75 47.8125V10.3125C3.7505 9.81537 3.9482 9.33875 4.29972 8.98722C4.65125 8.6357 5.12787 8.438 5.625 8.4375H9.45938C9.68146 9.74692 10.3594 10.9356 11.3733 11.7934C12.3873 12.6513 13.6719 13.1229 15 13.125H28.125C29.4531 13.1227 30.7376 12.651 31.7515 11.7932C32.7654 10.9354 33.4434 9.74684 33.6656 8.4375H37.5C37.9971 8.438 38.4738 8.6357 38.8253 8.98722C39.1768 9.33875 39.3745 9.81537 39.375 10.3125V26.25C39.375 26.7473 39.5725 27.2242 39.9242 27.5758C40.2758 27.9275 40.7527 28.125 41.25 28.125C41.7473 28.125 42.2242 27.9275 42.5758 27.5758C42.9275 27.2242 43.125 26.7473 43.125 26.25V10.3125C43.1233 8.82119 42.5301 7.39146 41.4756 6.33694C40.421 5.28243 38.9913 4.68924 37.5 4.6875H33.6656C33.4434 3.37816 32.7654 2.18956 31.7515 1.33178C30.7376 0.473998 29.4531 0.00227918 28.125 0L15 0C13.6719 0.00209276 12.3873 0.473745 11.3733 1.33156C10.3594 2.18937 9.68146 3.37808 9.45938 4.6875H5.625C4.13369 4.68924 2.70396 5.28243 1.64944 6.33694C0.594927 7.39146 0.00173682 8.82119 0 10.3125L0 47.8125C0.00173682 49.3038 0.594927 50.7335 1.64944 51.7881C2.70396 52.8426 4.13369 53.4358 5.625 53.4375H25.3125C25.8098 53.4375 26.2867 53.24 26.6383 52.8883C26.99 52.5367 27.1875 52.0598 27.1875 51.5625C27.1875 51.0652 26.99 50.5883 26.6383 50.2367C26.2867 49.885 25.8098 49.6875 25.3125 49.6875ZM13.125 5.625C13.1255 5.12787 13.3232 4.65125 13.6747 4.29972C14.0262 3.9482 14.5029 3.7505 15 3.75H28.125C28.6221 3.7505 29.0988 3.9482 29.4503 4.29972C29.8018 4.65125 29.9995 5.12787 30 5.625V7.5C29.9995 7.99713 29.8018 8.47375 29.4503 8.82528C29.0988 9.1768 28.6221 9.3745 28.125 9.375H15C14.5029 9.3745 14.0262 9.1768 13.6747 8.82528C13.3232 8.47375 13.1255 7.99713 13.125 7.5V5.625ZM17.1094 20.2144L12.4219 23.9644C12.0615 24.2524 11.6077 24.3975 11.1471 24.3719C10.6865 24.3463 10.2515 24.1518 9.92531 23.8256L7.11281 21.0131C6.77127 20.6595 6.58228 20.1859 6.58655 19.6942C6.59082 19.2026 6.78801 18.7324 7.13565 18.3847C7.48329 18.0371 7.95357 17.8399 8.44519 17.8356C8.9368 17.8313 9.41043 18.0203 9.76406 18.3619L11.3897 19.9875L14.7666 17.2856C15.1554 16.9813 15.6486 16.8427 16.1391 16.8998C16.6295 16.9569 17.0776 17.2052 17.3861 17.5907C17.6946 17.9763 17.8386 18.4679 17.7868 18.959C17.735 19.45 17.4916 19.9017 17.1094 20.2144ZM33.75 19.6875C34.2473 19.6875 34.7242 19.885 35.0758 20.2367C35.4275 20.5883 35.625 21.0652 35.625 21.5625C35.625 22.0598 35.4275 22.5367 35.0758 22.8883C34.7242 23.24 34.2473 23.4375 33.75 23.4375H21.5625C21.0652 23.4375 20.5883 23.24 20.2367 22.8883C19.885 22.5367 19.6875 22.0598 19.6875 21.5625C19.6875 21.0652 19.885 20.5883 20.2367 20.2367C20.5883 19.885 21.0652 19.6875 21.5625 19.6875H33.75ZM17.1094 30.5269L12.4219 34.2769C12.0615 34.5649 11.6077 34.71 11.1471 34.6844C10.6865 34.6588 10.2515 34.4643 9.92531 34.1381L7.11281 31.3256C6.77127 30.972 6.58228 30.4984 6.58655 30.0067C6.59082 29.5151 6.78801 29.0449 7.13565 28.6972C7.48329 28.3496 7.95357 28.1524 8.44519 28.1481C8.9368 28.1438 9.41043 28.3328 9.76406 28.6744L11.3897 30.3L14.7666 27.5981C15.1554 27.2938 15.6486 27.1552 16.1391 27.2123C16.6295 27.2694 17.0776 27.5177 17.3861 27.9032C17.6946 28.2888 17.8386 28.7804 17.7868 29.2715C17.735 29.7625 17.4916 30.2142 17.1094 30.5269ZM29.0625 31.875C29.0625 32.3723 28.865 32.8492 28.5133 33.2008C28.1617 33.5525 27.6848 33.75 27.1875 33.75H21.5625C21.0652 33.75 20.5883 33.5525 20.2367 33.2008C19.885 32.8492 19.6875 32.3723 19.6875 31.875C19.6875 31.3777 19.885 30.9008 20.2367 30.5492C20.5883 30.1975 21.0652 30 21.5625 30H27.1875C27.6848 30 28.1617 30.1975 28.5133 30.5492C28.865 30.9008 29.0625 31.3777 29.0625 31.875ZM17.4019 38.2041C17.5557 38.3962 17.6701 38.6168 17.7386 38.8532C17.8072 39.0896 17.8285 39.3372 17.8013 39.5818C17.7741 39.8265 17.699 40.0634 17.5803 40.279C17.4616 40.4946 17.3016 40.6847 17.1094 40.8384L12.4219 44.5884C12.0616 44.8769 11.6076 45.0223 11.1468 44.9968C10.686 44.9714 10.2507 44.7769 9.92438 44.4506L7.11188 41.6381C6.77033 41.2845 6.58134 40.8109 6.58561 40.3192C6.58988 39.8276 6.78708 39.3574 7.13472 39.0097C7.48236 38.6621 7.95263 38.4649 8.44425 38.4606C8.93587 38.4563 9.4095 38.6453 9.76312 38.9869L11.3897 40.6125L14.7666 37.9106C14.9588 37.7569 15.1794 37.6425 15.4159 37.5741C15.6523 37.5056 15.8999 37.4844 16.1445 37.5116C16.3892 37.5389 16.626 37.6141 16.8416 37.7329C17.0572 37.8517 17.2482 38.0118 17.4019 38.2041ZM27.1875 42.1875C27.1875 42.6848 26.99 43.1617 26.6383 43.5133C26.2867 43.865 25.8098 44.0625 25.3125 44.0625H21.5625C21.0652 44.0625 20.5883 43.865 20.2367 43.5133C19.885 43.1617 19.6875 42.6848 19.6875 42.1875C19.6875 41.6902 19.885 41.2133 20.2367 40.8617C20.5883 40.51 21.0652 40.3125 21.5625 40.3125H25.3125C25.8098 40.3125 26.2867 40.51 26.6383 40.8617C26.99 41.2133 27.1875 41.6902 27.1875 42.1875Z"
          fill="url(#paint0_linear_3367_11406)"
        />
        <path
          d="M58.8637 35.7778L45.7387 30.1528C45.5055 30.0522 45.2541 30.0002 45 30.0002C44.7459 30.0002 44.4945 30.0522 44.2613 30.1528L31.1363 35.7778C30.7991 35.9222 30.5118 36.1623 30.3098 36.4685C30.1078 36.7746 30.0001 37.1332 30 37.5V52.5C29.9999 52.8669 30.1075 53.2258 30.3095 53.5321C30.5115 53.8384 30.799 54.0787 31.1363 54.2231L44.2613 59.8481C44.4946 59.9485 44.746 60.0003 45 60.0003C45.254 60.0003 45.5054 59.9485 45.7387 59.8481L58.8637 54.2231C59.201 54.0787 59.4885 53.8384 59.6905 53.5321C59.8925 53.2258 60.0001 52.8669 60 52.5V37.5C59.9999 37.1332 59.8922 36.7746 59.6902 36.4685C59.4882 36.1623 59.2009 35.9222 58.8637 35.7778ZM45 41.0859L43.0491 40.2497L51.105 36.5316L53.3653 37.5L45 41.0859ZM45 33.915L46.4644 34.5431L38.4084 38.2612L36.6338 37.5L45 33.915ZM33.75 40.3434L36.5625 41.5491V46.875C36.5625 47.3723 36.76 47.8492 37.1117 48.2008C37.4633 48.5525 37.9402 48.75 38.4375 48.75C38.9348 48.75 39.4117 48.5525 39.7633 48.2008C40.115 47.8492 40.3125 47.3723 40.3125 46.875V43.1559L43.125 44.3616V55.2816L33.75 51.2644V40.3434ZM46.875 55.2816V44.3625L56.25 40.3444V51.2653L46.875 55.2816ZM25.3125 49.6875H5.625C5.12787 49.687 4.65125 49.4893 4.29972 49.1378C3.9482 48.7863 3.7505 48.3096 3.75 47.8125V10.3125C3.7505 9.81537 3.9482 9.33875 4.29972 8.98722C4.65125 8.6357 5.12787 8.438 5.625 8.4375H9.45938C9.68146 9.74692 10.3594 10.9356 11.3733 11.7934C12.3873 12.6513 13.6719 13.1229 15 13.125H28.125C29.4531 13.1227 30.7376 12.651 31.7515 11.7932C32.7654 10.9354 33.4434 9.74684 33.6656 8.4375H37.5C37.9971 8.438 38.4738 8.6357 38.8253 8.98722C39.1768 9.33875 39.3745 9.81537 39.375 10.3125V26.25C39.375 26.7473 39.5725 27.2242 39.9242 27.5758C40.2758 27.9275 40.7527 28.125 41.25 28.125C41.7473 28.125 42.2242 27.9275 42.5758 27.5758C42.9275 27.2242 43.125 26.7473 43.125 26.25V10.3125C43.1233 8.82119 42.5301 7.39146 41.4756 6.33694C40.421 5.28243 38.9913 4.68924 37.5 4.6875H33.6656C33.4434 3.37816 32.7654 2.18956 31.7515 1.33178C30.7376 0.473998 29.4531 0.00227918 28.125 0L15 0C13.6719 0.00209276 12.3873 0.473745 11.3733 1.33156C10.3594 2.18937 9.68146 3.37808 9.45938 4.6875H5.625C4.13369 4.68924 2.70396 5.28243 1.64944 6.33694C0.594927 7.39146 0.00173682 8.82119 0 10.3125L0 47.8125C0.00173682 49.3038 0.594927 50.7335 1.64944 51.7881C2.70396 52.8426 4.13369 53.4358 5.625 53.4375H25.3125C25.8098 53.4375 26.2867 53.24 26.6383 52.8883C26.99 52.5367 27.1875 52.0598 27.1875 51.5625C27.1875 51.0652 26.99 50.5883 26.6383 50.2367C26.2867 49.885 25.8098 49.6875 25.3125 49.6875ZM13.125 5.625C13.1255 5.12787 13.3232 4.65125 13.6747 4.29972C14.0262 3.9482 14.5029 3.7505 15 3.75H28.125C28.6221 3.7505 29.0988 3.9482 29.4503 4.29972C29.8018 4.65125 29.9995 5.12787 30 5.625V7.5C29.9995 7.99713 29.8018 8.47375 29.4503 8.82528C29.0988 9.1768 28.6221 9.3745 28.125 9.375H15C14.5029 9.3745 14.0262 9.1768 13.6747 8.82528C13.3232 8.47375 13.1255 7.99713 13.125 7.5V5.625ZM17.1094 20.2144L12.4219 23.9644C12.0615 24.2524 11.6077 24.3975 11.1471 24.3719C10.6865 24.3463 10.2515 24.1518 9.92531 23.8256L7.11281 21.0131C6.77127 20.6595 6.58228 20.1859 6.58655 19.6942C6.59082 19.2026 6.78801 18.7324 7.13565 18.3847C7.48329 18.0371 7.95357 17.8399 8.44519 17.8356C8.9368 17.8313 9.41043 18.0203 9.76406 18.3619L11.3897 19.9875L14.7666 17.2856C15.1554 16.9813 15.6486 16.8427 16.1391 16.8998C16.6295 16.9569 17.0776 17.2052 17.3861 17.5907C17.6946 17.9763 17.8386 18.4679 17.7868 18.959C17.735 19.45 17.4916 19.9017 17.1094 20.2144ZM33.75 19.6875C34.2473 19.6875 34.7242 19.885 35.0758 20.2367C35.4275 20.5883 35.625 21.0652 35.625 21.5625C35.625 22.0598 35.4275 22.5367 35.0758 22.8883C34.7242 23.24 34.2473 23.4375 33.75 23.4375H21.5625C21.0652 23.4375 20.5883 23.24 20.2367 22.8883C19.885 22.5367 19.6875 22.0598 19.6875 21.5625C19.6875 21.0652 19.885 20.5883 20.2367 20.2367C20.5883 19.885 21.0652 19.6875 21.5625 19.6875H33.75ZM17.1094 30.5269L12.4219 34.2769C12.0615 34.5649 11.6077 34.71 11.1471 34.6844C10.6865 34.6588 10.2515 34.4643 9.92531 34.1381L7.11281 31.3256C6.77127 30.972 6.58228 30.4984 6.58655 30.0067C6.59082 29.5151 6.78801 29.0449 7.13565 28.6972C7.48329 28.3496 7.95357 28.1524 8.44519 28.1481C8.9368 28.1438 9.41043 28.3328 9.76406 28.6744L11.3897 30.3L14.7666 27.5981C15.1554 27.2938 15.6486 27.1552 16.1391 27.2123C16.6295 27.2694 17.0776 27.5177 17.3861 27.9032C17.6946 28.2888 17.8386 28.7804 17.7868 29.2715C17.735 29.7625 17.4916 30.2142 17.1094 30.5269ZM29.0625 31.875C29.0625 32.3723 28.865 32.8492 28.5133 33.2008C28.1617 33.5525 27.6848 33.75 27.1875 33.75H21.5625C21.0652 33.75 20.5883 33.5525 20.2367 33.2008C19.885 32.8492 19.6875 32.3723 19.6875 31.875C19.6875 31.3777 19.885 30.9008 20.2367 30.5492C20.5883 30.1975 21.0652 30 21.5625 30H27.1875C27.6848 30 28.1617 30.1975 28.5133 30.5492C28.865 30.9008 29.0625 31.3777 29.0625 31.875ZM17.4019 38.2041C17.5557 38.3962 17.6701 38.6168 17.7386 38.8532C17.8072 39.0896 17.8285 39.3372 17.8013 39.5818C17.7741 39.8265 17.699 40.0634 17.5803 40.279C17.4616 40.4946 17.3016 40.6847 17.1094 40.8384L12.4219 44.5884C12.0616 44.8769 11.6076 45.0223 11.1468 44.9968C10.686 44.9714 10.2507 44.7769 9.92438 44.4506L7.11188 41.6381C6.77033 41.2845 6.58134 40.8109 6.58561 40.3192C6.58988 39.8276 6.78708 39.3574 7.13472 39.0097C7.48236 38.6621 7.95263 38.4649 8.44425 38.4606C8.93587 38.4563 9.4095 38.6453 9.76312 38.9869L11.3897 40.6125L14.7666 37.9106C14.9588 37.7569 15.1794 37.6425 15.4159 37.5741C15.6523 37.5056 15.8999 37.4844 16.1445 37.5116C16.3892 37.5389 16.626 37.6141 16.8416 37.7329C17.0572 37.8517 17.2482 38.0118 17.4019 38.2041ZM27.1875 42.1875C27.1875 42.6848 26.99 43.1617 26.6383 43.5133C26.2867 43.865 25.8098 44.0625 25.3125 44.0625H21.5625C21.0652 44.0625 20.5883 43.865 20.2367 43.5133C19.885 43.1617 19.6875 42.6848 19.6875 42.1875C19.6875 41.6902 19.885 41.2133 20.2367 40.8617C20.5883 40.51 21.0652 40.3125 21.5625 40.3125H25.3125C25.8098 40.3125 26.2867 40.51 26.6383 40.8617C26.99 41.2133 27.1875 41.6902 27.1875 42.1875Z"
          fill="url(#paint1_linear_3367_11406)"
        />
      </g>
      <defs>
        <linearGradient
          id="paint0_linear_3367_11406"
          x1="43.6041"
          y1="7.3425"
          x2="13.7728"
          y2="59.0109"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#EA5A6B" />
          <stop offset="0.181" stopColor="#B9569B" />
          <stop offset="0.351" stopColor="#9052C1" />
          <stop offset="0.439" stopColor="#8151D0" />
          <stop offset="0.483" stopColor="#7D51D4" />
          <stop offset="0.806" stopColor="#634FED" />
          <stop offset={1} stopColor="#5A4EF6" />
        </linearGradient>
        <linearGradient id="paint1_linear_3367_11406" x1={30} y1="60.0003" x2={30} y2={0} gradientUnits="userSpaceOnUse">
          <stop stopColor="#3A89FF" />
          <stop offset={1} stopColor="#81CFFF" />
        </linearGradient>
        <clipPath id="clip0_3367_11406">
          <rect width={60} height={60} fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
});

// Constants
const BANNER_ID_SUFFIX = 'bad52e2a-9253-4e44-ba84-4f7594efb673';

const DEFAULT_ANALYTICS_DATA = {
  data: {
    metrics: {
      totalShipments: 0,
      totalShipmentsChange: 0,
      trackedShipments: 0,
      trackedShipmentsPercentage: 0,
      trackedShipmentsChange: 0,
      untrackedShipments: 0,
      untrackedShipmentsPercentage: 0,
      untrackedShipmentsChange: 0,
      exceptionalShipments: 0,
      exceptionalShipmentsPercentage: 0,
      exceptionalShipmentsChange: 0,
      expiredShipments: 0,
      expiredShipmentsPercentage: 0,
      expiredShipmentsChange: 0,
    },
    shipmentsOverTime: {
      categories: ['01-01', '01-02', '01-03', '01-04', '01-05'],
      series: [{ name: 'total_shipments', data: [0, 0, 0, 0, 0] }],
    },
    shipmentsByCarrier: {
      categories: [],
      series: [],
    },
    shipmentsByLocation: {
      categories: [],
      series: [],
    },
    shipmentsByStatus: {
      categories: [],
      series: [],
    },
  },
};

const BANNER_FIRST_COLUMN_ITEMS = [
  {
    id: '1',
    label: 'How much you shipped during this period',
    showBullet: true,
  },
  {
    id: '2',
    label: 'How many shipments were tracked, delayed, or exceptional',
    showBullet: true,
  },
  {
    id: '3',
    label: 'How your overall shipping activity is trending over time',
    showBullet: true,
  },
];

interface ShipmentsOverviewProps {
  start: Date;
  end: Date;
  title: string;
}

const ShipmentsOverview = ({ start, end, title }: ShipmentsOverviewProps) => {
  const shipmentFilter = useSelector(shipmentFilterSelector);
  const shipmentDatePicker = useSelector(datePickerSelector);
  const shipmentTitleDatePicker = useSelector(titleDatePickerSelector);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { navigateToPlan } = useUpgradeTracking();
  const { shop } = useSelector(tokenSelector);
  const id = `${shop}:${BANNER_ID_SUFFIX}`;
  const { permanentDismissIds } = useSelector((state: RootState) => state.common);
  const isLock = false;
  const { data, isLoading, refetch } = useGetAnalyticsShipmentOverviewQuery({
    fromDate: start.toISOString(),
    toDate: end.toISOString(),
    dateType: ranges.find((range) => range.title === title)?.alias,
  });

  // Manual refresh state management
  const [isPollingLoading, setIsPollingLoading] = useState(false);
  const [pollingProgress, setPollingProgress] = useState<number>(0);
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [canRefresh, setCanRefresh] = useState(true);
  const autoIncrementRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const showRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-increment progress visually while processing
  useEffect(() => {
    if (isPollingLoading) {
      autoIncrementRef.current = setInterval(() => {
        setPollingProgress((prev) => {
          if (prev >= 95) return prev;
          const step = prev < 30 ? 0.8 : prev < 60 ? 0.4 : 0.15;
          return Math.min(prev + step, 95);
        });
      }, 600);
    } else {
      if (autoIncrementRef.current) clearInterval(autoIncrementRef.current);
    }
    return () => {
      if (autoIncrementRef.current) clearInterval(autoIncrementRef.current);
    };
  }, [isPollingLoading]);

  // Derive processing state directly from data to avoid one-render flash
  const isCurrentlyProcessing = !isLoading && data?.data?.state === 'processing';
  const finalIsLoading = isLoading || isCurrentlyProcessing;
  const finalData = data;

  // Sync polling state with the latest response state
  useEffect(() => {
    if (!data?.data) return;

    if (data.data.state === 'processing') {
      setIsPollingLoading(true);
      setPollingProgress(data.data.progress || 1);
      showRefreshTimeoutRef.current = setTimeout(() => setShowRefreshButton(true), 2000);
    } else {
      if (showRefreshTimeoutRef.current) clearTimeout(showRefreshTimeoutRef.current);
      setIsPollingLoading(false);
      setShowRefreshButton(false);
      setPollingProgress(0);
    }
  }, [data]);

  // Manual check job status — refetch the main endpoint to get updated state/progress
  const checkJobStatus = async () => {
    setCanRefresh(false);
    try {
      await refetch();
    } finally {
      setCanRefresh(true);
    }
  };

  const metrics = useMemo(() => {
    const metrics = finalData?.data?.metrics;
    const totalShipments = metrics?.totalShipments || 0;
    const totalShipmentsChange = metrics?.totalShipmentsChange || 0;

    const trackedShipments = metrics?.trackedShipments || 0;
    const trackedShipmentsChange = metrics?.trackedShipmentsChange || 0;
    const trackedShipmentsPercentage = metrics?.trackedShipmentsPercentage || 0;

    const untrackedShipments = metrics?.untrackedShipments || 0;
    const untrackedShipmentsChange = metrics?.untrackedShipmentsChange || 0;
    const untrackedShipmentsPercentage = metrics?.untrackedShipmentsPercentage || 0;

    const exceptionalShipments = metrics?.exceptionalShipments || 0;
    const exceptionalShipmentsChange = metrics?.exceptionalShipmentsChange || 0;
    const exceptionalShipmentsPercentage = metrics?.exceptionalShipmentsPercentage || 0;

    const expiredShipments = metrics?.expiredShipments || 0;
    const expiredShipmentsChange = metrics?.expiredShipmentsChange || 0;
    const expiredShipmentsPercentage = metrics?.expiredShipmentsPercentage || 0;
    return [
      {
        title: 'Total shipments',
        value: totalShipments,
        change: totalShipmentsChange >= 0 ? `+${totalShipmentsChange}%` : `${totalShipmentsChange}%`,
        trend: totalShipmentsChange > 0 ? 'up' : totalShipmentsChange === 0 ? 'steady' : 'down',
        percentage: '',
      },
      {
        title: 'Tracked shipments',
        value: trackedShipments,
        change: trackedShipmentsChange >= 0 ? `+${trackedShipmentsChange}%` : `${trackedShipmentsChange}%`,
        trend: trackedShipmentsChange > 0 ? 'up' : trackedShipmentsChange === 0 ? 'steady' : 'down',
        percentage: `${trackedShipmentsPercentage}% of total`,
        tooltip: 'Fulfilled shipments with tracking numbers added',
        direct: true,
        onClick: () => {
          dispatch(
            shipmentSlice.actions.handleChangeTableFilter({ ...initialStateFilter, type: EShipmentType.TRACKED_SHIPMENT }),
          );
          dispatch(shipmentSlice.actions.handleDatePicker({ start, end }));
          dispatch(shipmentSlice.actions.handleTitleDatePicker(title));
          setTimeout(() => {
            navigate(PATH.SHIPMENT, {
              state: {
                from: 'shipmentsOverview',
                previousFilter: { shipmentFilter, shipmentDatePicker, shipmentTitleDatePicker },
              },
            });
          });
        },
      },
      {
        title: 'Untracked shipments',
        value: untrackedShipments,
        change: untrackedShipmentsChange >= 0 ? `+${untrackedShipmentsChange}%` : `${untrackedShipmentsChange}%`,
        trend: untrackedShipmentsChange > 0 ? 'up' : untrackedShipmentsChange === 0 ? 'steady' : 'down',
        percentage: `${untrackedShipmentsPercentage}% of total`,
        tooltip: 'Fulfilled shipments without tracking numbers.',
        direct: true,
        onClick: () => {
          dispatch(
            shipmentSlice.actions.handleChangeTableFilter({ ...initialStateFilter, type: EShipmentType.UNTRACKED_SHIPMENT }),
          );
          dispatch(shipmentSlice.actions.handleDatePicker({ start, end }));
          dispatch(shipmentSlice.actions.handleTitleDatePicker(title));
          setTimeout(() => {
            navigate(PATH.SHIPMENT, {
              state: {
                from: 'shipmentsOverview',
                previousFilter: { shipmentFilter, shipmentDatePicker, shipmentTitleDatePicker },
              },
            });
          });
        },
      },
      {
        title: 'Exceptional shipments',
        value: exceptionalShipments,
        change: exceptionalShipmentsChange >= 0 ? `+${exceptionalShipmentsChange}%` : `${exceptionalShipmentsChange}%`,
        trend: exceptionalShipmentsChange > 0 ? 'up' : exceptionalShipmentsChange === 0 ? 'steady' : 'down',
        percentage: `${exceptionalShipmentsPercentage}% of total`,
        tooltip: 'Shipments can be stuck, delayed, returned, etc.',
        direct: true,
        onClick: () => {
          dispatch(
            shipmentSlice.actions.handleChangeTableFilter({
              ...initialStateFilter,
              statusIn: [ShipmentStatus.Exception, ShipmentStatus.FailedAttempt],
            }),
          );
          dispatch(shipmentSlice.actions.handleDatePicker({ start, end }));
          dispatch(shipmentSlice.actions.handleTitleDatePicker(title));
          setTimeout(() => {
            navigate(PATH.SHIPMENT, {
              state: {
                from: 'shipmentsOverview',
                previousFilter: { shipmentFilter, shipmentDatePicker, shipmentTitleDatePicker },
              },
            });
          });
        },
      },
      {
        title: 'Expired shipments',
        value: expiredShipments,
        change: expiredShipmentsChange >= 0 ? `+${expiredShipmentsChange}%` : `${expiredShipmentsChange}%`,
        trend: expiredShipmentsChange > 0 ? 'up' : expiredShipmentsChange === 0 ? 'steady' : 'down',
        percentage: `${expiredShipmentsPercentage}% of total`,
        tooltip: 'Undelivered shipments with no tracking updates in the last 60 days.',
        direct: true,
        onClick: () => {
          dispatch(
            shipmentSlice.actions.handleChangeTableFilter({
              ...initialStateFilter,
              statusIndex: 2,
              status: ShipmentStatus.Expired,
            }),
          );
          dispatch(shipmentSlice.actions.handleDatePicker({ start, end }));
          dispatch(shipmentSlice.actions.handleTitleDatePicker(title));
          setTimeout(() => {
            navigate(PATH.SHIPMENT, {
              state: {
                from: 'shipmentsOverview',
                previousFilter: { shipmentFilter, shipmentDatePicker, shipmentTitleDatePicker },
              },
            });
          });
        },
      },
    ];
  }, [finalData, dispatch, navigate, start, end, title, shipmentFilter, shipmentDatePicker, shipmentTitleDatePicker]);

  const shipmentsOvertimeData: DataSeries[] = useMemo(() => {
    let shipmentsOverTime = finalData?.data?.shipmentsOverTime;
    if (!shipmentsOverTime || !shipmentsOverTime.categories || !shipmentsOverTime.series) {
      shipmentsOverTime = DEFAULT_ANALYTICS_DATA.data.shipmentsOverTime;
    }
    const { categories, series } = shipmentsOverTime;
    return series.map((seriesItem: any) => {
      return {
        name: camelToRegularText(seriesItem.name),
        data: categories.map((category: any, index: number) => ({
          key: convertUtcHourToLocalTimezone(category),
          value: Number(seriesItem.data?.[index] || 0),
        })),
        color: '#2196F3',
      };
    });
  }, [finalData]);

  // Calculate chart states
  const getChartState = (dataLength: number): any => {
    if (finalIsLoading) return 'Loading';
    // if (dataLength === 0) return 'Error';
    return 'Success';
  };

  const shipmentsByCarrierData: DataSeries[] = useMemo(() => {
    let shipmentsByCarrier = finalData?.data?.shipmentsByCarrier;
    if (!shipmentsByCarrier || !shipmentsByCarrier.categories || !shipmentsByCarrier.series) {
      shipmentsByCarrier = DEFAULT_ANALYTICS_DATA.data.shipmentsByCarrier;
    }
    const { categories, series } = shipmentsByCarrier;
    return categories.map((category: any, index: number) => ({
      name: capitalizeWords(category),
      data: [
        {
          key: 'shipments',
          value: Number(series[index] || 0),
        },
      ],
    }));
  }, [finalData]);

  const shipmentsByLocationData: DataSeries[] = useMemo(() => {
    let shipmentsByLocation = finalData?.data?.shipmentsByLocation;
    if (!shipmentsByLocation || !shipmentsByLocation.categories || !shipmentsByLocation.series) {
      shipmentsByLocation = DEFAULT_ANALYTICS_DATA.data.shipmentsByLocation;
    }
    const { categories, series } = shipmentsByLocation;
    return categories.map((category: any, index: number) => ({
      name: category,
      data: [
        {
          key: 'shipments',
          value: Number(series[index] || 0),
        },
      ],
    }));
  }, [finalData]);

  const shipmentsByStatusData: DataSeries[] = useMemo(() => {
    let shipmentsByStatus = finalData?.data?.shipmentsByStatus;
    if (!shipmentsByStatus || !shipmentsByStatus.categories || !shipmentsByStatus.series) {
      shipmentsByStatus = DEFAULT_ANALYTICS_DATA.data.shipmentsByStatus;
    }
    const { categories, series } = shipmentsByStatus;

    return categories.map((category: any, index: number) => ({
      name: camelToRegularText(category === ShipmentStatus.NotFound ? 'no_info' : category),
      data: [
        {
          key: 'shipments',
          value: Number(series[index] || 0),
        },
      ],
    }));
  }, [finalData]);

  const secondColumnItems = useMemo(
    () => [
      {
        id: '1',
        label: 'Not find this helpful?',
        tone: 'subdued',
        showBullet: false,
      },
      {
        id: '2',
        label: <Button onClick={handleOpenChat}>Feedback us</Button>,
        showBullet: false,
      },
    ],
    [],
  );

  const formatTooltipTitle = (title: string): string => {
    return formatAnalyticsTooltipTitle(title);
  };

  const handleRefreshClick = () => {
    checkJobStatus();
  };

  return (
    <StyledShipmentsOverview>
      {isLock && (
        <Banner tone="info">
          <InlineStack>
            This is SAMPLE data set. Upgrade to Launch plan or above to unlock your insights.
            <Button
              variant="primary"
              onClick={() => navigateToPlan({
                trigger_type: 'banner',
                trigger_element: 'Upgrade Now',
                feature: 'shipment_analytics',
              })}
            >
              Upgrade Now
            </Button>
          </InlineStack>
        </Banner>
      )}
      {/* Refresh Button */}
      {showRefreshButton && data?.data?.state === 'processing' && (
        <Box
          background="bg-surface-secondary"
          borderRadius="300"
          padding="400"
          borderWidth="025"
          borderColor="border"
        >
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="050">
                <Text as="p" variant="bodyMd" fontWeight="semibold">
                  Data is being calculated
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Your analytics are being processed in the background.
                </Text>
              </BlockStack>
              <Button
                icon={RefreshIcon}
                onClick={handleRefreshClick}
                loading={!canRefresh}
                variant="primary"
                size="slim"
              >
                Check status
              </Button>
            </InlineStack>
            <ProgressBar progress={pollingProgress} size="small" tone="primary" animated />
          </BlockStack>
        </Box>
      )}

      {/* Info Banner */}
      {!permanentDismissIds.includes(id) && (
        <S.BannerContainer>
          <S.BannerContent>
            <S.Content $justifyContent="flex-start" $gap="60px" $gapResponsive="16px">
              <InlineStack gap="800" blockAlign="center" wrap={false}>
                <S.IconWrapper>
                  <CustomIcon />
                </S.IconWrapper>
                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd" tone="subdued">
                    What to look for
                  </Text>
                  <Text as="p" variant="bodyLg" fontWeight="semibold">
                    Monitor daily shipment flow
                  </Text>
                </BlockStack>
              </InlineStack>

              {BANNER_FIRST_COLUMN_ITEMS.length > 0 && (
                <BlockStack gap="200">
                  {BANNER_FIRST_COLUMN_ITEMS.map((item) => (
                    <S.ListItem key={item.id}>
                      {item.showBullet !== false && <S.Bullet>•</S.Bullet>}
                      <Text as="span" variant="bodyMd">
                        {item.label}
                      </Text>
                    </S.ListItem>
                  ))}
                </BlockStack>
              )}

              {secondColumnItems.length > 0 && (
                <BlockStack gap="200">
                  {secondColumnItems.map((item) => (
                    <S.ListItem key={item.id}>
                      {item.showBullet !== false && <S.Bullet>•</S.Bullet>}
                      <Text as="span" variant="bodyMd" tone={item.tone as any}>
                        {item.label}
                      </Text>
                    </S.ListItem>
                  ))}
                </BlockStack>
              )}
            </S.Content>

            <S.CloseButton onClick={() => dispatch(setPermanentDismissId(id))}>
              <XIcon />
            </S.CloseButton>
          </S.BannerContent>
        </S.BannerContainer>
      )}

      {/* Metrics Cards */}
      <InlineGrid columns={{ xs: 1, sm: 2, md: 3, lg: 5, xl: 5 }} gap="400">
        {metrics.map((metric, index) => (
          <div key={index} className={`card-wrapper ${isLock ? 'locked' : ''}`}>
            <Card>
              <div className="metric-card">
                <InlineStack>
                  {metric.tooltip ? (
                    <Tooltip content={metric.tooltip} dismissOnMouseOut hasUnderline>
                      <Text variant="bodyMd" as="p" fontWeight="semibold">
                        {metric.title}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text variant="bodyMd" as="p" fontWeight="semibold">
                      {metric.title}
                    </Text>
                  )}
                </InlineStack>
                <Box paddingBlockStart="100">
                  {finalIsLoading ? (
                    <SkeletonBodyText lines={5} />
                  ) : (
                    <>
                      <InlineStack blockAlign="center" gap="200">
                        <Text variant="heading2xl" as="h3">
                          {metric.value}
                        </Text>
                        <Badge tone={metric.trend === 'up' ? 'success' : metric.trend === 'steady' ? 'info' : 'critical'}>
                          {metric.change}
                        </Badge>
                      </InlineStack>
                      <Text variant="bodyMd" as="p">
                        {metric.percentage}
                      </Text>
                      {metric.direct && (
                        <span className="metric-direct">
                          <Button icon={ArrowRightIcon} variant="plain" onClick={metric.onClick} />
                        </span>
                      )}
                    </>
                  )}
                </Box>
              </div>
            </Card>
          </div>
        ))}
      </InlineGrid>

      {/* Shipments Over Time Chart */}
      <div className={`chart-wrapper ${isLock ? 'locked' : ''}`}>
        <Card>
          <Text as="h2" variant="headingSm">
            Shipments Over Time
          </Text>
          <Box paddingBlockStart="600">
            <div style={{ height: '400px' }}>
              <BarChart
                data={shipmentsOvertimeData}
                state={getChartState(shipmentsOvertimeData.length)}
                theme="Light"
                isAnimated
                showLegend
                type="stacked"
                tooltipOptions={{
                  titleFormatter: (title) => formatTooltipTitle(title?.toString() || ''),
                }}
              />
            </div>
          </Box>
        </Card>
      </div>

      {/* Bottom Charts */}
      <InlineGrid columns={{ xs: 1, sm: 1, md: 1, lg: 2, xl: 2 }} gap="400">
        <div className={`chart-wrapper ${isLock ? 'locked' : ''}`}>
          <Card>
            <Text as="h2" variant="headingSm">
              Shipments by Status
            </Text>
            <Box paddingBlockStart="600">
              <div style={{ height: '400px' }}>
                <DonutChart
                  data={shipmentsByStatusData}
                  state={getChartState(shipmentsByStatusData.length)}
                  theme="Light"
                  isAnimated
                  renderInnerValueContent={(values) => {
                    const { activeValue, totalValue } = values;
                    if (!activeValue)
                      return (
                        <Text variant="headingLg" as="span" fontWeight="regular" alignment="center">
                          Total: <strong>{totalValue}</strong>
                        </Text>
                      );
                    const percent = ((activeValue / totalValue) * 100).toFixed(2);
                    return (
                      <BlockStack>
                        <Text variant="headingLg" as="span" alignment="center">
                          {percent}%
                        </Text>
                        <Text variant="headingLg" as="span" fontWeight="regular" alignment="center">
                          <strong>{activeValue}</strong>
                        </Text>
                      </BlockStack>
                    );
                  }}
                />
              </div>
            </Box>
          </Card>
        </div>

        <div className={`chart-wrapper ${isLock ? 'locked' : ''}`}>
          <Card>
            <Text as="h2" variant="headingSm">
              Shipments by Carrier
            </Text>
            <Box paddingBlockStart="600">
              <div style={{ height: '400px' }}>
                <DonutChart
                  data={shipmentsByCarrierData}
                  state={getChartState(shipmentsByCarrierData.length)}
                  theme="Light"
                  isAnimated
                  renderInnerValueContent={(values) => {
                    const { activeValue, totalValue } = values;
                    if (!activeValue)
                      return (
                        <Text variant="headingLg" as="span" fontWeight="regular" alignment="center">
                          Total: <strong>{totalValue}</strong>
                        </Text>
                      );
                    const percent = ((activeValue / totalValue) * 100).toFixed(2);
                    return (
                      <BlockStack>
                        <Text variant="headingLg" as="span" alignment="center">
                          {percent}%
                        </Text>
                        <Text variant="headingLg" as="span" fontWeight="regular" alignment="center">
                          <strong>{activeValue}</strong>
                        </Text>
                      </BlockStack>
                    );
                  }}
                />
              </div>
            </Box>
          </Card>
        </div>

        <div className={`chart-wrapper ${isLock ? 'locked' : ''}`}>
          <Card>
            <Text as="h2" variant="headingSm">
              Shipments by Origin and Destination Location
            </Text>
            <Box paddingBlockStart="600">
              <div style={{ height: '400px' }}>
                <DonutChart
                  data={shipmentsByLocationData}
                  state={getChartState(shipmentsByLocationData.length)}
                  theme="Light"
                  isAnimated
                  renderInnerValueContent={(values) => {
                    const { activeValue, totalValue } = values;
                    if (!activeValue)
                      return (
                        <Text variant="headingLg" as="span" fontWeight="regular" alignment="center">
                          Total: <strong>{totalValue}</strong>
                        </Text>
                      );
                    const percent = ((activeValue / totalValue) * 100).toFixed(2);
                    return (
                      <BlockStack>
                        <Text variant="headingLg" as="span" alignment="center">
                          {percent}%
                        </Text>
                        <Text variant="headingLg" as="span" fontWeight="regular" alignment="center">
                          <strong>{activeValue}</strong>
                        </Text>
                      </BlockStack>
                    );
                  }}
                />
              </div>
            </Box>
          </Card>
        </div>
      </InlineGrid>
    </StyledShipmentsOverview>
  );
};

export default ShipmentsOverview;
